<template>
    <b-modal
        :active="open"
        @close="$emit('close')"
        trap-focus
        :destroy-on-hide="true"
        close-button-aria-label="Close"
        aria-modal>

        <div class="card">
            <div class="card-header">
                <p class="card-header-title">
                    <i class="fal fa-fw mr-3" :class="mode === 'announcement' ? 'fa-bullhorn' : 'fa-camera'"></i>
                    {{ mode === 'announcement' ? 'New Announcement' : 'New Post' }}
                </p>
            </div>

            <div class="card-content">
                <b-field>
                    <b-input
                        v-model="body"
                        type="textarea"
                        rows="4"
                        :placeholder="mode === 'announcement' ? 'What does the group need to know?' : 'Share a moment from the trip...'"
                    />
                </b-field>

                <!-- Poll editor (announcements only) -->
                <div v-if="mode === 'announcement'" class="mt-4">
                    <b-switch v-model="withPoll" size="is-small">Add a quick poll</b-switch>

                    <div v-if="withPoll" class="mt-3 poll-editor">
                        <b-field label="Answer type" label-position="inside" class="mb-3">
                            <b-select v-model="pollType" size="is-small" expanded>
                                <option value="single">Pick one</option>
                                <option value="multi">Pick several</option>
                                <option value="text">Write an answer</option>
                            </b-select>
                        </b-field>

                        <!-- A free-form poll has no options by definition. -->
                        <template v-if="pollType !== 'text'">
                            <b-field v-for="(option, i) in options" :key="i" class="mb-2">
                                <b-input v-model="options[i]" :placeholder="'Option ' + (i + 1)" size="is-small" expanded/>
                                <p class="control" v-if="options.length > 2">
                                    <b-button size="is-small" @click="options.splice(i, 1)"><i class="fas fa-times"></i></b-button>
                                </p>
                            </b-field>
                            <div>
                                <b-button size="is-small" type="is-text" @click="options.push('')" v-if="options.length < 10">
                                    <i class="fas fa-plus mr-2"></i>Add option
                                </b-button>
                            </div>

                            <b-field class="mt-2 mb-2" v-if="pollType === 'multi'">
                                <b-select v-model="maxChoices" size="is-small" expanded>
                                    <option :value="0">Any number of choices</option>
                                    <option v-for="n in maxChoiceRange" :key="n" :value="n">Up to {{ n }} choices</option>
                                </b-select>
                            </b-field>

                            <b-switch v-model="allowOther" size="is-small" class="mt-2">
                                Add an "Other" option they can type into
                            </b-switch>
                        </template>

                        <!-- Presets, not a date picker: this composer is used
                             on a phone mid-trip, and every real deadline here
                             is "by tonight" or "by tomorrow". Roger has the
                             exact-datetime control. -->
                        <b-field label="Stop accepting answers" label-position="inside" class="mt-3 mb-0">
                            <b-select v-model="closesInHours" size="is-small" expanded>
                                <option :value="0">Never — leave it open</option>
                                <option :value="3">In 3 hours</option>
                                <option :value="12">In 12 hours</option>
                                <option :value="24">In 24 hours</option>
                                <option :value="72">In 3 days</option>
                            </b-select>
                        </b-field>
                    </div>

                    <div class="mt-4">
                        <b-switch v-model="schedule" size="is-small" type="is-warning">
                            <i class="fas fa-calendar-exclamation mr-1"></i>
                            Schedule update — sends a notification to the group
                        </b-switch>
                    </div>
                </div>

                <!-- Photos & videos (wall only) -->
                <div v-if="mode === 'wall'" class="mt-4">
                    <!-- One photo per post for participants — multi-photo
                         posts are a staff affordance (Roger compose); the
                         server caps non-staff at one as well. -->
                    <file-pond
                        name="file"
                        ref="pond"
                        label-idle="Drop or click to add a photo or video..."
                        :allow-multiple="false"
                        :allowRevert="false"
                        accepted-file-types="image/*, video/mp4, video/quicktime, video/webm"
                        :before-add-file="checkFileSize"
                        @processfilestart="uploading++"
                        @processfile="handleFilePondProcess"
                        :server="{
                            url: apiUrl,
                            timeout: 300000,
                            process: {
                                url: 'app/experience/upload',
                                method: 'POST',
                                headers: {
                                    'Authorization': token
                                },
                                withCredentials: false
                            }
                        }"
                    />
                    <div class="mt-2">
                        <b-tag type="is-info" size="is-small" class="mr-2" v-for="(photo, i) in photos" :key="i">
                            <i class="fas fa-fw mr-1" :class="photo.file_mime && photo.file_mime.indexOf('video/') === 0 ? 'fa-video' : 'fa-image'"></i>{{ photo.file_name }}
                        </b-tag>
                    </div>
                </div>
            </div>

            <footer class="card-footer">
                <div class="card-footer-item">
                    <b-button type="is-primary" @click="save" :disabled="!valid || saving || uploading > 0" :loading="saving">
                        <i class="fas mr-3" :class="uploading > 0 ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                        {{ uploading > 0 ? 'Uploading...' : 'Post' }}
                    </b-button>
                </div>
            </footer>
        </div>
    </b-modal>
</template>

<script>
    import {mapGetters} from 'vuex';
    import {client as http, url} from '@/http_client';

    import vueFilePond from 'vue-filepond';
    import 'filepond/dist/filepond.min.css';
    import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
    import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
    import FilePondPluginImagePreview from 'filepond-plugin-image-preview';

    const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview);

    export default {
        name: 'Composer',
        components: {FilePond},
        props: {
            mode: {type: String, required: true}, // 'announcement' | 'wall'
            open: {type: Boolean, default: false},
            regId: {type: Number, required: true}
        },
        data: function() {
            return {
                apiUrl: url,
                body: '',
                withPoll: false,
                pollType: 'single',
                options: ['', ''],
                maxChoices: 0,
                allowOther: false,
                closesInHours: 0,
                schedule: false,
                photos: [],
                saving: false,
                uploading: 0
            };
        },
        computed: {
            ...mapGetters(['token']),
            cleanOptions: function() {
                return this.options.map(o => o.trim()).filter(Boolean);
            },
            // "Up to N" only makes sense strictly between 1 and every option;
            // at the top end it is just "any number".
            maxChoiceRange: function() {
                const count = Math.max(this.cleanOptions.length, 2);
                return Array.from({length: count - 1}, (_, i) => i + 1);
            },
            // Resolved at send time from the preset, as an absolute UTC
            // instant — the server stores a datetime, not a duration.
            closesAt: function() {
                if (!this.withPoll || !this.closesInHours) return null;
                return new Date(Date.now() + this.closesInHours * 3600 * 1000).toISOString();
            },
            // A free-form poll needs no options; a choice poll needs two.
            pollReady: function() {
                if (!this.withPoll) return false;
                return this.pollType === 'text' || this.cleanOptions.length >= 2;
            },
            valid: function() {
                if (this.mode === 'announcement') {
                    if (this.withPoll && !this.pollReady) return false;
                    return !!this.body.trim() || this.pollReady;
                }
                return !!this.body.trim() || this.photos.length > 0;
            }
        },
        methods: {
            save: function() {
                if (this.saving) return;
                this.saving = true;

                let request;
                if (this.mode === 'announcement') {
                    request = http.post(`/app/experience/${this.regId}/announcements`, {
                        body: this.body,
                        announcement_type: this.schedule ? 'schedule' : 'general',
                        poll_type: this.withPoll ? this.pollType : null,
                        options: this.withPoll && this.pollType !== 'text' ? this.cleanOptions : [],
                        poll_max_choices: this.pollType === 'multi' ? this.maxChoices : 0,
                        poll_allow_other: this.withPoll && this.pollType !== 'text' && this.allowOther,
                        poll_closes_at: this.closesAt
                    }, {force: true});
                } else {
                    request = http.post(`/app/experience/${this.regId}/wall`, {
                        body: this.body,
                        photos: this.photos
                    }, {force: true});
                }

                request.then(() => {
                    this.$notify({
                        group: 'notify',
                        type: 'success',
                        title: 'Posted',
                        text: this.mode === 'announcement' ? 'Your announcement is live.' : 'Your post is on the wall.'
                    });
                    this.reset();
                    this.$emit('saved');
                }).catch(() => {
                    this.saving = false;
                });
            },
            reset: function() {
                this.body = '';
                this.withPoll = false;
                this.pollType = 'single';
                this.options = ['', ''];
                this.maxChoices = 0;
                this.allowOther = false;
                this.closesInHours = 0;
                this.schedule = false;
                this.photos = [];
                this.saving = false;
                this.uploading = 0;
            },
            // Runs before FilePond even queues the file — a friendly stop for
            // oversized videos instead of a failed 200MB upload attempt (the
            // server's multer limit is the same number).
            checkFileSize: function(file) {
                if (file.file.size > 200 * 1024 * 1024) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: 'File Too Large',
                        text: 'Videos can be up to 200MB. Try trimming it or recording at a lower quality.'
                    });
                    return false;
                }
                return true;
            },
            // Fires for success AND failure; the pending counter is what keeps
            // the Post button locked until every drop has actually landed — a
            // photo must never be silently missing from the published post.
            handleFilePondProcess: function(err, file) {
                this.uploading = Math.max(0, this.uploading - 1);
                if (err) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: 'Upload Failed',
                        text: 'That file didn\'t upload — please remove it and try again.'
                    });
                    return;
                }
                try {
                    const uploaded = JSON.parse(file.serverId);
                    // Replace, don't accumulate — FilePond swaps the single
                    // file, and the post must carry only the current one.
                    this.photos = [{
                        file: uploaded.file_name,
                        file_name: uploaded.file_original_name,
                        file_mime: uploaded.file_mime
                    }];
                } catch (e) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: 'Upload Failed',
                        text: 'That file didn\'t upload — please remove it and try again.'
                    });
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .poll-editor {
        border-left: 3px solid #e7e7e7;
        padding-left: 0.8rem;
    }
</style>
